import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from '@tanstack/react-query'
import {
    fetchAuthUrlYandex,
    fetchYandexCounters,
    fetchYandexGoals,
    fetchYandexProfile,
    setYandexMetrics,
    yandexLogout,
} from './yandexIntegration.service'
import {
    SetMetricsDto,
    YandexCounter,
    YandexGoal,
    YandexProfile,
} from '@synapex/shared'

export function useMutationYandexAuthUrlQuery(siteId: number) {
    return useMutation<{ authUrl: string }, Error>({
        mutationFn: () => fetchAuthUrlYandex(siteId),
    })
}

export function useGetYandexProfileQuery(
    siteId: number,
    options?: UseQueryOptions<YandexProfile, Error>
) {
    return useQuery<YandexProfile, Error>({
        queryKey: ['yandexProfile', siteId],
        queryFn: () => fetchYandexProfile(siteId),
        staleTime: 1000 * 60 * 5,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        retry: false,
        ...options,
    })
}

export function useMutationYandexLogout(siteId: number) {
    const queryClient = useQueryClient()

    return useMutation<any, Error>({
        mutationFn: () => yandexLogout(siteId),
        onSuccess: () => {
            queryClient.removeQueries({
                queryKey: ['yandexProfile', siteId],
            })
        },
    })
}

export function useMutationSetYandexMetrics() {
    const queryClient = useQueryClient()

    return useMutation<
        void,
        Error,
        {
            siteId: number
            body: SetMetricsDto
        }
    >({
        mutationFn: ({ siteId, body }) => setYandexMetrics(siteId, body),
        onSuccess: (_, { siteId }) => {
            queryClient.invalidateQueries({
                queryKey: ['yandexProfile', siteId],
            })
        },
    })
}

export function useGetYandexCounters(
    siteId: number,
    options?: Omit<UseQueryOptions<YandexCounter[], Error>, 'queryKey'>
) {
    return useQuery<YandexCounter[], Error>({
        queryKey: ['yandexCounters', siteId],
        queryFn: () => fetchYandexCounters(siteId),
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        ...options,
    })
}

export function useGetYandexGoals(
    siteId: number,
    counterId: number,
    options?: Omit<UseQueryOptions<YandexGoal[], Error>, 'queryKey'>
) {
    return useQuery<YandexGoal[], Error>({
        queryKey: ['yandexGoals', siteId, counterId],
        queryFn: () => fetchYandexGoals(siteId, counterId),
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        ...options,
    })
}
